/* ⚠️ AS CORES SAÍRAM DO ARQUIVO em 19/08 e passaram a vir do CSS
   (`currentColor`). Eram azul chapado mais degradê — e o sistema tem acento
   ÚNICO e não usa degradê em lugar nenhum. A distinção entre água e comida
   passa a ser a FORMA (a gota), que é o caminho que o design system indica
   quando falta cor: "estado é forma mais cor, nunca cor sozinha".
   Quem pinta agora é quem monta, com `color:` — igual aos SVGs da marca. */
import * as React from "react";

export interface DropProps {
  /** Size in px. Canonical: 22 (header), 18 (list), 13 (week), 96 (modal), 220 (celebration) */
  size?: number;
  /** Progress 0-100. <100 renders outlined progress ring; 100 renders filled 7-layer drop. */
  percent?: number;
  /** Extra className (e.g. for drop-shadow filters at large sizes) */
  className?: string;
  /**
   * Whether to render the <defs> gradients inline. Set true ONLY if `LiquidMercuryDefs`
   * is NOT already mounted in the app root. Mounting both globally + per-instance
   * creates duplicate SVG IDs (`dropBody`, `dropRefraction`, etc.) and produces
   * undefined gradient resolution (browser-dependent). In OnDiet, `LiquidMercuryDefs`
   * is already mounted in App.tsx — leave this `false`.
   */
  withDefs?: boolean;
}

const GRADIENT_DEFS = (
  <defs>
    <linearGradient id="dropBody" x1="35%" y1="0%" x2="65%" y2="100%">
      <stop offset="0%" stopColor="currentColor" />
      <stop offset="25%" stopColor="currentColor" />
      <stop offset="55%" stopColor="currentColor" />
      <stop offset="85%" stopColor="currentColor" />
      <stop offset="100%" stopColor="currentColor" />
    </linearGradient>
    <radialGradient id="dropRefraction" cx="50%" cy="85%" r="45%" fx="50%" fy="85%">
      <stop offset="0%" stopColor="currentColor" stopOpacity={0.75} />
      <stop offset="60%" stopColor="currentColor" stopOpacity={0.15} />
      <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
    </radialGradient>
    <linearGradient id="dropHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor="currentColor" stopOpacity={0.85} />
      <stop offset="100%" stopColor="currentColor" stopOpacity={0.15} />
    </linearGradient>
    {/* ⚠️ ERA AZUL CRAVADO (`rgba(14,165,233,…)` = #0ea5e9) e escapou da
        migração de tokens justamente por estar dentro do SVG, não no CSS.
        Azul não existe na paleta — o sistema tem UM acento, a Brasa.
        `currentColor` faz o contorno seguir a cor de quem monta a gota:
        Brasa no atalho do Plano, tinta na folha de água. Os outros dois
        gradientes deste mesmo arquivo já faziam assim. */}
    <linearGradient id="dropOutline" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor="currentColor" stopOpacity={0.35} />
      <stop offset="100%" stopColor="currentColor" stopOpacity={0.12} />
    </linearGradient>
  </defs>
);

/** Mount this ONCE per page (in App root). Other LiquidMercuryDrop instances reference these IDs. */
export const LiquidMercuryDefs: React.FC = React.memo(() => (
  <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden>
    {GRADIENT_DEFS}
  </svg>
));
LiquidMercuryDefs.displayName = "LiquidMercuryDefs";

export const LiquidMercuryDrop: React.FC<DropProps> = React.memo(({
  size = 22,
  percent = 0,
  className,
  withDefs = false,
}) => {
  const isFull = percent >= 100;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
      {withDefs && GRADIENT_DEFS}
      {isFull ? (
        <>
          {/* 7-layer Liquid Mercury */}
          <ellipse cx="12" cy="22.5" rx="6" ry="0.7" fill="currentColor" fillOpacity={0.18} />
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" fill="url(#dropBody)" />
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" fill="url(#dropRefraction)" />
          <path d="M12 2.69 L 14.5 5.2 Q 13.5 4.5, 12 4.5 Q 10.5 4.5, 9.5 5.2 Z" fill="rgba(255,255,255,0.5)" />
          <path d="M14.5 9.5 Q 16.5 11, 17 13.5 Q 17.2 16, 15.3 18.2 Q 14.5 17.2, 15 15.5 Q 15.5 13.5, 14.8 11.5 Q 14.2 10.3, 14.5 9.5 Z" fill="url(#dropHighlight)" />
          <ellipse cx="10.5" cy="9" rx="0.8" ry="2.2" fill="rgba(255,255,255,0.55)" transform="rotate(-22 10.5 9)" />
          <circle cx="13.6" cy="8" r="0.35" fill="white" opacity={0.95} />
        </>
      ) : (
        <>
          {/* Outline base */}
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"
                fill="none" stroke="url(#dropOutline)" strokeWidth={1.4} />
          {/* Progress ring */}
          <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"
                fill="none" stroke="currentColor" strokeWidth={1.4}
                pathLength={100}
                strokeDasharray={`${Math.max(0, Math.min(100, percent))} 100`}
                strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
});
LiquidMercuryDrop.displayName = "LiquidMercuryDrop";
